import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import styled from "styled-components"
import { ModalFooter } from "reactstrap"
import { Paragraph, Tel } from "@pga/pga-component-library"
import { redirectToLogout } from "../../lib/auth"
import { Button } from "../Button"
import { Modal, ModalBody, Title } from "../Modal"
import { path } from "ramda"
import { isAffiliate, isAssociate, isStudent, pgaComTld } from "../utils"
import { TERMINATE_CODE } from "./terminateCodes"
import "./modal.css"
import { COACH_TOOLS_API } from "../../apolloClient"
import { flags, useFeatureFlags } from "../../lib/feature-flags"
import { redirectToMemberApplications } from "../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/associateApplicationEligibility"
import { AFFILIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/affiliateApplicationEligibility"
import BecomeAssociateModal from "../widgets/CourseCurriculum/BecomeAssociateModal"

const ModalWrapper = styled(Modal)`
  .modal-content {
    border-radius: 0;
    border: none;
    @media screen and (max-width: 575.98px) {
      text-align: center;
    }
  }
  .modal-footer {
    width: 100%;
    justify-content: center;
    padding-top: 1.5rem;
    padding-bottom: 0;
  }
`

const Body = styled(ModalBody)`
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Telephone = styled(Tel)`
  font-size: 24px;
  margin-bottom: 24px;
`

const MEMBERSHIP_NUMBER = "(800) 474-2776"

/** Terminated auth `me` may lack program type; mirror useMembershipJourney fallback. */
const meWithTerminationTypeFallback = me => {
  if (!me) return me
  const programType = me.mainProgramTypeCode || me.type
  if (programType) return me
  const terminationType = me.termination?.type
  if (!terminationType) return me
  return {
    ...me,
    type: terminationType === "AF" ? "AP" : terminationType,
  }
}

const TerminatedModal = ({ me, duesCycle }) => {
  const [open] = useState(true)
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)
  const [isPtmApplicationsRedirectEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_PTM_APPLICATIONS_REDIRECT,
  ])
  const redirectEnabled = !!isPtmApplicationsRedirectEnabled

  const duesTermination =
    path(["termination", "terminateCd"])(me) === TERMINATE_CODE.NON_PAYMENT
  const reEstablishmentFee = duesCycle?.order?.lineItems?.some(
    item => item?.name === "Re-Establishment Fee",
  )
  const isDuesReestablishmentBranch = duesTermination && reEstablishmentFee
  // Non-payment terminations should not surface application CTAs (pay dues path).
  const suppressApplicationCtas = duesTermination

  const eligibilityMe = meWithTerminationTypeFallback(me)
  const studentViewer = isStudent(eligibilityMe)
  const associateViewer = isAssociate(eligibilityMe)
  const affiliateViewer = isAffiliate(eligibilityMe)
  const shouldFetchAssociateEligibility =
    redirectEnabled &&
    !suppressApplicationCtas &&
    (studentViewer || associateViewer || affiliateViewer)
  const shouldFetchAffiliateEligibility =
    redirectEnabled && !suppressApplicationCtas && studentViewer

  const {
    data: associateEligibilityData,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
  } = useQuery(ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchAssociateEligibility,
  })

  const {
    data: affiliateEligibilityData,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
  } = useQuery(AFFILIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchAffiliateEligibility,
  })

  const showBecomeAssociateButton =
    shouldFetchAssociateEligibility &&
    !associateEligibilityLoading &&
    !associateEligibilityError &&
    associateEligibilityData?.associateApplicationEligibility?.eligible === true

  const showBecomeAffiliateButton =
    shouldFetchAffiliateEligibility &&
    !affiliateEligibilityLoading &&
    !affiliateEligibilityError &&
    affiliateEligibilityData?.affiliateApplicationEligibility?.eligible === true

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  return (
    <>
      <ModalWrapper isOpen={open} toggle={() => {}} size="md" centered>
        {isDuesReestablishmentBranch ? (
          <Body>
            <Title className="mb-4">Membership Not Current</Title>
            <Paragraph fw="normal" className="mb-2 ml-4">
              Pay your outstanding dues to re-establish your membership.
            </Paragraph>
            <Paragraph fw="normal" className="ml-4">
              For additional support contact PGA Membership Services at:
            </Paragraph>
            <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
            <ModalFooter>
              <Button
                href={`https://${pgaComTld}/member/dues/overview`}
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
              >
                Pay Dues Now
              </Button>
              <Button color="primary" onClick={() => redirectToLogout()}>
                Return to Public Content
              </Button>
            </ModalFooter>
          </Body>
        ) : (
          <Body>
            <Title className="mb-4">Membership Not Current</Title>
            <Paragraph fw="normal" className="mb-2">
              Your membership is{" "}
              <Paragraph fw="normal" className="d-inline text-danger">
                <u>not current.</u>
              </Paragraph>
            </Paragraph>
            <Paragraph fw="normal">
              Please contact PGA Membership Services at:
            </Paragraph>
            <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
            <ModalFooter>
              {showBecomeAssociateButton && (
                <Button
                  color="primary"
                  className="m-2"
                  onClick={() => setIsBecomeAssociateModalOpen(true)}
                >
                  Become Associate
                </Button>
              )}
              {showBecomeAffiliateButton && (
                <Button
                  color="primary"
                  className="m-2"
                  onClick={redirectToMemberApplications}
                >
                  Become Affiliate
                </Button>
              )}
              <Button
                color={
                  showBecomeAssociateButton || showBecomeAffiliateButton
                    ? "secondary"
                    : "primary"
                }
                outline={showBecomeAssociateButton || showBecomeAffiliateButton}
                onClick={() => redirectToLogout()}
              >
                Return to Public Content
              </Button>
            </ModalFooter>
          </Body>
        )}
      </ModalWrapper>
      <BecomeAssociateModal
        isOpen={isBecomeAssociateModalOpen}
        onClose={() => setIsBecomeAssociateModalOpen(false)}
        onApply={redirectToMemberApplications}
      />
    </>
  )
}

export default TerminatedModal
